import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  CreateNewFolder as FolderIcon,
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: 400,
      borderRadius: 8,
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  titleIcon: {
    color: theme.palette.text.secondary,
  },
  titleText: {
    fontSize: "1rem",
    fontWeight: 500,
    color: theme.palette.secondary.main,
  },
  closeButton: {
    color: theme.palette.grey[500],
    padding: 8,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: (props) => props.clientColor || theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: (props) => props.clientColor || theme.palette.primary.main,
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 2.5),
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    gap: theme.spacing(1),
  },
  cancelButton: {
    color: theme.palette.text.secondary,
  },
  createButton: {
    backgroundColor: (props) => props.clientColor || theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: (props) =>
        props.clientColor || theme.palette.primary.dark,
      opacity: 0.9,
    },
    "&:disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
}));

const FolderCreateDialog = ({
  open,
  onClose,
  onConfirm,
  parentFolderName,
  clientColor,
}) => {
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenario_manager.folder_create",
  });
  const [folderName, setFolderName] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setFolderName("");
      setError("");
      // Delay focus to ensure dialog is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleChange = (e) => {
    setFolderName(e.target.value);
    if (error) setError("");
  };

  const handleSubmit = () => {
    const trimmedName = folderName.trim();
    if (!trimmedName) {
      setError(t("name_required"));
      return;
    }
    onConfirm(trimmedName);
    setFolderName("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && folderName.trim()) {
      handleSubmit();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const handleClose = () => {
    setFolderName("");
    setError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth="sm"
    >
      <Box className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <FolderIcon className={classes.titleIcon} />
          <Typography className={classes.titleText}>{t("title")}</Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        {parentFolderName && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            {t("creating_inside")}
            <strong>{parentFolderName}</strong>
          </Typography>
        )}
        <TextField
          inputRef={inputRef}
          autoFocus
          fullWidth
          variant="outlined"
          label={t("folder_name")}
          placeholder={t("placeholder")}
          value={folderName}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error}
          className={classes.textField}
          size="small"
        />
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.cancelButton}>
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!folderName.trim()}
          className={classes.createButton}
          variant="contained"
        >
          {t("create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FolderCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  parentFolderName: PropTypes.string,
  clientColor: PropTypes.string,
};

export default FolderCreateDialog;
